import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2, Edit2, TrendingUp, Wallet, Archive, ArchiveRestore, Calculator, PiggyBank, ChevronDown, ChevronRight } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, ReferenceLine,
} from 'recharts';
import { useBudget } from '../../context/BudgetContext';
import { formatCurrency, formatDate, todayStr, uuid } from '../../utils/formatting';
import {
  getBucketBalance, getBucketBalanceInMain, getBucketProjection, monthsToGoal,
  getHistoricalBalanceSeries, inferAnnualInterestRate, getActualInterest,
  getGroupInterestWindows, getGroupMonthlyInterest, getGroupBalanceHistory,
  getGroupInterestBetween, lookbackStartDate,
} from '../../utils/calculations';
import { CHART_COLORS } from '../../utils/constants';
import { ConfirmDialog, Modal, ProgressBar, EmptyState, SearchableSelect, ListRow, CardList, BudgetIcon } from '../Common/Common';
import { getAllCurrencies, getCurrencyInfo, CURRENCY_LOCALE_MAP } from '../../utils/currencies';
import { ensureRatesForRange, getLatestRate, getRateForMonth } from '../../utils/exchangeRates';
import EmojiPicker, { EmojiStyle, Theme } from 'emoji-picker-react';
import { useIsPhone } from '../../hooks/useIsPhone';

/** Format `v` using a bucket's own currency (falls back to app settings). */
function fmtForBucket(bucket, v, settings) {
  const code = bucket?.currency || settings.currency;
  const locale = (bucket?.currency && CURRENCY_LOCALE_MAP[bucket.currency]) || settings.locale;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency: code,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(v);
  } catch {
    return formatCurrency(v, settings);
  }
}

/** Symbol for a bucket's currency (or app's main). */
function bucketSymbol(bucket, settings) {
  const code = bucket?.currency || settings.currency;
  return getCurrencyInfo(code)?.symbol || settings.currencySymbol || code;
}

function Savings() {
  const {
    buckets, transactions, addBucket, updateBucket, deleteBucket, addBucketAdjustment, deleteAdjustment,
    updateAdjustment, deleteTransaction, openEntrySheet,
    settings, showToast,
  } = useBudget();
  const mainCurrency = settings.currency || 'ILS';
  const fmt = v => formatCurrency(v, settings);
  const fmtFor = (b, v) => fmtForBucket(b, v, settings);
  const isPhone = useIsPhone();
  const [ratesTick, setRatesTick] = useState(0); // bumps when FX cache updates to re-render

  // Prefetch FX rates for every foreign-currency bucket present. Covers from
  // the earliest event date on each bucket through today. Swallows errors —
  // on network failure we degrade to whatever's cached.
  useEffect(() => {
    const todayYM = new Date().toISOString().slice(0, 7);
    const foreign = buckets.filter(b => b.currency && b.currency !== mainCurrency);
    if (foreign.length === 0) return;
    (async () => {
      for (const b of foreign) {
        const dates = [b.createdDate].concat(
          (b.adjustments || []).map(a => a.date),
          transactions.filter(t => t.bucket === b.id).map(t => t.date)
        ).filter(Boolean).sort();
        const startYM = (dates[0] || todayYM).slice(0, 7);
        await ensureRatesForRange(b.currency, mainCurrency, startYM, todayYM);
      }
      setRatesTick(t => t + 1);
    })();
  }, [buckets, transactions, mainCurrency]);

  const [showAddBucket, setShowAddBucket] = useState(false);
  const [showLoanCalc,    setShowLoanCalc]    = useState(false);
  const [showSavingsCalc, setShowSavingsCalc] = useState(false);
  const [adjModal,      setAdjModal]      = useState(null); // { bucketId, bucketName, currentBalance, initialType? }
  const [editModal,     setEditModal]     = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [detailBucket,  setDetailBucket]  = useState(null); // bucket id | null
  const [detailGroup,   setDetailGroup]   = useState(null); // group name | null
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [showArchived,  setShowArchived]  = useState(false);

  const activeBuckets   = useMemo(() => buckets.filter(b => !b.archived), [buckets]);
  const archivedBuckets = useMemo(() => buckets.filter(b => b.archived),  [buckets]);
  const visibleBuckets  = showArchived ? archivedBuckets : activeBuckets;

  const UNGROUPED = '__ungrouped__';
  const existingGroups = useMemo(
    () => [...new Set(buckets.map(b => (b.group || '').trim()).filter(Boolean))].sort(),
    [buckets]
  );
  const hasGroups = useMemo(() => visibleBuckets.some(b => (b.group || '').trim()), [visibleBuckets]);
  const colorIdxOf = useMemo(() => {
    const m = {}; visibleBuckets.forEach((b, i) => { m[b.id] = i; }); return m;
  }, [visibleBuckets]);
  // Named groups alphabetically, "Ungrouped" last.
  const groupedBuckets = useMemo(() => {
    const map = new Map();
    visibleBuckets.forEach(b => {
      const g = (b.group || '').trim() || UNGROUPED;
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(b);
    });
    return [...map.entries()].sort((a, b) => {
      if (a[0] === UNGROUPED) return 1;
      if (b[0] === UNGROUPED) return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [visibleBuckets]);
  const toggleGroup = name => setCollapsedGroups(prev => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  // Balances in the bucket's own currency
  const bucketBalances = useMemo(() => {
    const m = {};
    buckets.forEach(b => { m[b.id] = getBucketBalance(b, transactions); });
    return m;
  }, [buckets, transactions]);

  // Balances converted to the app's main currency (used for summary/pie)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bucketBalancesMain = useMemo(() => {
    const m = {};
    buckets.forEach(b => { m[b.id] = getBucketBalanceInMain(b, transactions, mainCurrency); });
    return m;
  }, [buckets, transactions, mainCurrency, ratesTick]);

  const totalBalance = useMemo(
    () => activeBuckets.reduce((s, b) => {
      const v = bucketBalancesMain[b.id];
      return s + (typeof v === 'number' ? v : 0);
    }, 0),
    [activeBuckets, bucketBalancesMain]
  );
  const totalGoal = useMemo(
    () => activeBuckets.reduce((s, b) => {
      const t = b.target || 0;
      if (!b.currency || b.currency === mainCurrency) return s + t;
      const rate = getLatestRate(b.currency, mainCurrency);
      return s + (rate != null ? t * rate : t);
    }, 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeBuckets, mainCurrency, ratesTick]
  );

  const allocationData = useMemo(() =>
    activeBuckets.map((b, i) => ({
      name: b.name,
      value: Math.max(0, bucketBalancesMain[b.id] || 0),
      color: CHART_COLORS[i % CHART_COLORS.length],
    })).filter(x => x.value > 0),
    [activeBuckets, bucketBalancesMain]
  );

  // Keep detail modal synced when the underlying bucket changes (e.g. after adding an adjustment)
  const detailBucketLive = detailBucket ? buckets.find(b => b.id === detailBucket) || null : null;
  const detailBucketIdx  = detailBucket ? buckets.findIndex(b => b.id === detailBucket) : -1;

  // Members of the open group-detail modal (live, so adjustments reflect immediately)
  const detailGroupMembers = detailGroup
    ? activeBuckets.filter(b => ((b.group || '').trim() || UNGROUPED) === detailGroup)
    : [];

  function renderBucketCard(b, idx) {
    const bal           = bucketBalances[b.id] || 0;
    const balMain       = bucketBalancesMain[b.id] || 0;
    const isForeign     = b.currency && b.currency !== mainCurrency;
    const pct           = b.target > 0 ? Math.min(100, Math.round((bal / b.target) * 100)) : null;
    const ml            = b.target && b.monthlyTarget ? monthsToGoal(bal, b.target, b.monthlyTarget) : null;
    const interestEarned = (b.adjustments || []).filter(a => a.type === 'interest').reduce((s, a) => s + a.amount, 0);
    const adjCount      = (b.adjustments || []).length;

    return (
      <div
        key={b.id}
        className="card"
        onClick={() => setDetailBucket(b.id)}
        style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
        title="Click to view details"
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>
              {b.name}
              {isForeign && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>({b.currency})</span>}
            </div>
            <div
              className="mono savings-text"
              style={{ fontSize: 22, fontWeight: 500, cursor: b.archived ? 'default' : 'pointer', userSelect: 'none' }}
              title={b.archived ? undefined : 'Double-click to set balance'}
              onClick={e => e.stopPropagation()}
              onDoubleClick={e => {
                e.stopPropagation();
                if (b.archived) return;
                setAdjModal({ bucketId: b.id, bucketName: b.name, currentBalance: bal, initialType: 'set-balance' });
              }}
            >{fmtFor(b, bal)}</div>
            {isForeign && typeof balMain === 'number' && <div className="muted" style={{ fontSize: 11 }}>≈ {fmt(balMain)} {mainCurrency}</div>}
            {b.target > 0 && <div className="muted" style={{ fontSize: 12 }}>Goal: {fmtFor(b, b.target)}{pct !== null ? ` · ${pct}%` : ''}</div>}
          </div>
          <div className="savings-card-icon">
            <BudgetIcon emoji={b.emoji} color="var(--savings)" />
          </div>
        </div>

        {/* Progress */}
        {b.target > 0 && (
          <div style={{ marginBottom: 14 }}>
            <ProgressBar value={bal} max={b.target} color={CHART_COLORS[idx % CHART_COLORS.length]} />
          </div>
        )}

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', marginBottom: 14, fontSize: 12 }}>
          {b.monthlyTarget > 0 && <><span className="muted">Monthly target</span><span className="mono" style={{ textAlign: 'right' }}>{fmtFor(b, b.monthlyTarget)}</span></>}
          {ml !== null && <><span className="muted">Months to goal</span><span style={{ textAlign: 'right' }}>{ml === 0 ? 'Done' : `~${ml} months`}</span></>}
          {interestEarned > 0 && <><span className="muted">Interest earned</span><span className="mono savings-text" style={{ textAlign: 'right' }}>+{fmtFor(b, interestEarned)}</span></>}
          {adjCount > 0 && <><span className="muted">Adjustments</span><span style={{ textAlign: 'right' }}>{adjCount}</span></>}
        </div>

        {/* Actions */}
        <div className="divider" style={{ marginTop: 'auto' }} />
        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
          {!b.archived && (
            <button className="btn btn-sm" style={{ flex: 1 }} onClick={() => setAdjModal({ bucketId: b.id, bucketName: b.name, currentBalance: bal })}>
              + Adjustment
            </button>
          )}
          {!b.archived && (
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setEditModal(b)} title="Edit">
              <Edit2 size={13} />
            </button>
          )}
          {b.archived
            ? <button className="btn btn-ghost btn-icon btn-sm" onClick={() => { updateBucket(b.id, { archived: false }); showToast(`${b.name} restored`); }} title="Restore"><ArchiveRestore size={13} /></button>
            : <button className="btn btn-ghost btn-icon btn-sm" onClick={() => { updateBucket(b.id, { archived: true }); showToast(`${b.name} archived`); }} title="Archive"><Archive size={13} /></button>
          }
          <button className="btn btn-ghost btn-icon btn-sm btn-danger" onClick={() => setConfirmDelete(b)} title="Delete">
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary */}
      <div className="card" style={{ marginBottom: 16, background: 'var(--bg-secondary)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)', marginBottom: 4 }}>Total saved</div>
            <div className="mono savings-text" style={{ fontSize: 28, fontWeight: 500 }}>{fmt(totalBalance)}</div>
            {totalGoal > 0 && <div className="muted" style={{ fontSize: 12 }}>{Math.round((totalBalance / totalGoal) * 100)}% of total goal {fmt(totalGoal)}</div>}
          </div>
          <div className="savings-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {archivedBuckets.length > 0 && (
              <button className="btn btn-sm" onClick={() => setShowArchived(s => !s)}>
                {showArchived ? <><ArchiveRestore size={13} /> Active ({activeBuckets.length})</> : <><Archive size={13} /> Archived ({archivedBuckets.length})</>}
              </button>
            )}
            <button className="btn btn-sm" onClick={() => setShowLoanCalc(true)}><Calculator size={13} /> Loan calculator</button>
            <button className="btn btn-sm" onClick={() => setShowSavingsCalc(true)}><PiggyBank size={13} /> Savings predictor</button>
            {!showArchived && <button className="btn btn-primary" onClick={() => setShowAddBucket(true)}><Plus size={14} /> New bucket</button>}
          </div>
        </div>
        {totalGoal > 0 && <div style={{ marginTop: 12 }}><ProgressBar value={totalBalance} max={totalGoal} /></div>}
      </div>

      {/* Allocation Chart */}
      {buckets.length > 1 && allocationData.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">Savings allocation</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <ResponsiveContainer width={160} height={160}>
              <PieChart>
                <Pie data={allocationData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" paddingAngle={2}>
                  {allocationData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {allocationData.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: d.color, flexShrink: 0 }} />
                  <span>{d.name}</span>
                  <span className="mono savings-text">{fmt(d.value)}</span>
                  <span className="muted">({totalBalance > 0 ? Math.round((d.value / totalBalance) * 100) : 0}%)</span>
                </div>
              ))}
            </div>
          </div>
          <div className="divider" />
          {isPhone ? (
            <div className="savings-alloc-list">
              {activeBuckets.map(b => {
                const bal = bucketBalances[b.id] || 0;
                const pct = b.target > 0 ? Math.min(100, Math.round((bal / b.target) * 100)) : null;
                const isForeign = b.currency && b.currency !== mainCurrency;
                return (
                  <ListRow
                    key={b.id}
                    onClick={() => setDetailBucket(b.id)}
                    leading={<BudgetIcon emoji={b.emoji} color="var(--savings)" />}
                    title={<>{b.name}{isForeign && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>({b.currency})</span>}</>}
                    sub={`${fmtFor(b, bal)} / ${b.target ? fmtFor(b, b.target) : '—'}${pct !== null ? ` · ${pct}%` : ''}`}
                    value={b.monthlyTarget ? fmtFor(b, b.monthlyTarget) : '—'}
                    valueClass="savings-text"
                  />
                );
              })}
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="table-savings">
                <thead>
                  <tr>
                    <th>Bucket</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                    <th style={{ textAlign: 'right' }}>Goal</th>
                    <th style={{ textAlign: 'right' }}>Progress</th>
                    <th style={{ textAlign: 'right' }}>Monthly</th>
                    <th style={{ textAlign: 'right' }}>Months left</th>
                  </tr>
                </thead>
                <tbody>
                  {activeBuckets.map(b => {
                    const bal = bucketBalances[b.id] || 0;
                    const balMain = bucketBalancesMain[b.id] || 0;
                    const pct = b.target > 0 ? Math.min(100, Math.round((bal / b.target) * 100)) : null;
                    const ml  = b.target ? monthsToGoal(bal, b.target, b.monthlyTarget) : null;
                    const isForeign = b.currency && b.currency !== mainCurrency;
                    return (
                      <tr key={b.id}>
                        <td>{b.name}{isForeign && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>({b.currency})</span>}</td>
                        <td className="amount-cell savings-text">
                          {fmtFor(b, bal)}
                          {isForeign && typeof balMain === 'number' && <div className="muted" style={{ fontSize: 11 }}>≈ {fmt(balMain)} {mainCurrency}</div>}
                        </td>
                        <td className="amount-cell muted">{b.target ? fmtFor(b, b.target) : '—'}</td>
                        <td className="amount-cell">{pct !== null ? `${pct}%${pct >= 100 ? ' — done' : ''}` : '—'}</td>
                        <td className="amount-cell muted">{b.monthlyTarget ? fmtFor(b, b.monthlyTarget) : '—'}</td>
                        <td className="amount-cell muted">{ml === 0 ? 'Done' : ml ? `~${ml} mo` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Bucket Cards */}
      {visibleBuckets.length === 0
        ? (
          showArchived
            ? <EmptyState icon={<Archive size={28} />} title="No archived buckets" sub="Archived buckets appear here" />
            : <EmptyState icon={<Wallet size={28} />} title="No savings buckets yet" sub="Create your first savings goal to start tracking" action={<button className="btn btn-primary" onClick={() => setShowAddBucket(true)}><Plus size={14} /> Create bucket</button>} />
        )
        : !hasGroups
        ? (
          <div className="grid-auto">
            {visibleBuckets.map((b, idx) => renderBucketCard(b, idx))}
          </div>
        )
        : (
          groupedBuckets.map(([gname, members]) => {
            const isUng     = gname === UNGROUPED;
            const label     = isUng ? 'Ungrouped' : gname;
            const collapsed = collapsedGroups.has(gname);
            const groupTotal = members.reduce((s, b) => s + (bucketBalancesMain[b.id] || 0), 0);
            const win       = getGroupInterestWindows(members, mainCurrency);
            const ytdPct    = groupTotal > 0 ? (win.ytd / groupTotal) * 100 : null;
            return (
              <div key={gname} style={{ marginBottom: 16 }}>
                <div
                  className="card group-header"
                  style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10, cursor: isUng ? 'default' : 'pointer' }}
                  onClick={() => { if (!isUng) setDetailGroup(gname); }}
                  title={isUng ? undefined : 'Click to view group details'}
                >
                  <button
                    className="btn btn-ghost btn-icon btn-sm"
                    onClick={e => { e.stopPropagation(); toggleGroup(gname); }}
                    title={collapsed ? 'Expand' : 'Collapse'}
                  >
                    {collapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                  </button>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{label} <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>({members.length})</span></div>
                    {win.ytd !== 0 && (
                      <div className="muted" style={{ fontSize: 11 }}>
                        YTD +{fmt(win.ytd)}{ytdPct != null ? ` (${ytdPct.toFixed(1)}%)` : ''}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="mono savings-text" style={{ fontSize: 18, fontWeight: 500 }}>{fmt(groupTotal)}</div>
                  </div>
                </div>
                {!collapsed && (
                  <div className="grid-auto">
                    {members.map(b => renderBucketCard(b, colorIdxOf[b.id] || 0))}
                  </div>
                )}
              </div>
            );
          })
        )}

      {/* Add Bucket Modal */}
      {showAddBucket && (
        <AddBucketModal
          mainCurrency={mainCurrency}
          groups={existingGroups}
          onSave={(data) => { addBucket(data); showToast('Savings bucket created'); setShowAddBucket(false); }}
          onClose={() => setShowAddBucket(false)}
        />
      )}

      {/* Edit Bucket Modal */}
      {editModal && (
        <AddBucketModal
          initial={editModal}
          mainCurrency={mainCurrency}
          groups={existingGroups}
          onSave={(data) => { updateBucket(editModal.id, data); showToast('Bucket updated'); setEditModal(null); }}
          onClose={() => setEditModal(null)}
        />
      )}

      {/* Adjustment Modal */}
      {adjModal && (() => {
        const b = buckets.find(x => x.id === adjModal.bucketId);
        return (
          <AdjustmentModal
            bucket={b}
            bucketName={adjModal.bucketName}
            currentBalance={adjModal.currentBalance}
            initialType={adjModal.initialType}
            onSave={(adj) => { addBucketAdjustment(adjModal.bucketId, adj); showToast(`${adj.type} of ${fmtFor(b, adj.amount)} added`); setAdjModal(null); }}
            onClose={() => setAdjModal(null)}
            fmt={v => fmtFor(b, v)}
          />
        );
      })()}

      {/* Bucket Detail Modal */}
      {detailBucketLive && (
        <BucketDetailModal
          bucket={detailBucketLive}
          bal={bucketBalances[detailBucketLive.id] || 0}
          balMain={bucketBalancesMain[detailBucketLive.id] || 0}
          mainCurrency={mainCurrency}
          bucketIdx={detailBucketIdx}
          transactions={transactions}
          onClose={() => setDetailBucket(null)}
          onAddAdjustment={(opts = {}) => {
            setDetailBucket(null);
            setAdjModal({
              bucketId: detailBucketLive.id,
              bucketName: detailBucketLive.name,
              currentBalance: bucketBalances[detailBucketLive.id] || 0,
              initialType: opts.initialType,
            });
          }}
          onDeleteAdjustment={deleteAdjustment}
          onUpdateAdjustment={updateAdjustment}
          onDeleteTransaction={deleteTransaction}
          onEditTransaction={t => { setDetailBucket(null); openEntrySheet(t); }}
          onEdit={() => { setDetailBucket(null); setEditModal(detailBucketLive); }}
          fmt={fmt}
          fmtFor={fmtFor}
          settings={settings}
          ratesTick={ratesTick}
        />
      )}

      {/* Group Detail Modal */}
      {detailGroup && detailGroupMembers.length > 0 && (
        <GroupDetailModal
          groupName={detailGroup === UNGROUPED ? 'Ungrouped' : detailGroup}
          members={detailGroupMembers}
          bucketBalancesMain={bucketBalancesMain}
          transactions={transactions}
          mainCurrency={mainCurrency}
          fmt={fmt}
          ratesTick={ratesTick}
          onClose={() => setDetailGroup(null)}
          onOpenBucket={id => { setDetailGroup(null); setDetailBucket(id); }}
        />
      )}

      {/* Loan Calculator */}
      {showLoanCalc && <LoanCalculatorModal fmt={fmt} onClose={() => setShowLoanCalc(false)} />}

      {/* Savings Predictor */}
      {showSavingsCalc && <SavingsPredictorModal fmt={fmt} onClose={() => setShowSavingsCalc(false)} />}

      {/* Confirm Delete */}
      {confirmDelete && (
        <ConfirmDialog
          title="Delete bucket"
          message={`Delete "${confirmDelete.name}"? All adjustment history will be lost. Transactions will remain.`}
          danger
          onConfirm={() => { deleteBucket(confirmDelete.id); showToast('Bucket deleted'); setConfirmDelete(null); }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

/* ─── Group Detail Modal ─── */
function GroupDetailModal({
  groupName, members, bucketBalancesMain, transactions, mainCurrency, fmt, ratesTick,
  onClose, onOpenBucket,
}) {
  const LOOKBACKS = ['3M', '6M', '1Y', '2Y', '5Y', 'YTD', 'Custom'];
  const [lookback, setLookback] = useState('1Y');
  const todayIso = new Date().toISOString().slice(0, 10);
  const [customFrom, setCustomFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [customTo, setCustomTo]     = useState(todayIso);

  // Selected window as concrete [start, end] dates, used for both the chart and
  // the interest figure shown alongside it.
  const rangeStart = lookback === 'Custom'
    ? customFrom
    : lookback === 'YTD'
      ? `${new Date().getFullYear()}-01-01`
      : lookbackStartDate(lookback).toISOString().slice(0, 10);
  const rangeEnd = lookback === 'Custom' ? customTo : todayIso;

  const win = useMemo(() => getGroupInterestWindows(members, mainCurrency),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [members, mainCurrency, ratesTick]);
  const history = useMemo(
    () => getGroupBalanceHistory(members, transactions, lookback, mainCurrency,
      lookback === 'Custom' ? { start: customFrom, now: new Date(customTo) } : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [members, transactions, lookback, customFrom, customTo, mainCurrency, ratesTick]
  );
  const windowInterest = useMemo(
    () => getGroupInterestBetween(members, rangeStart, rangeEnd, mainCurrency),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [members, rangeStart, rangeEnd, mainCurrency, ratesTick]
  );
  const monthlyInterest = useMemo(
    () => getGroupMonthlyInterest(members, mainCurrency, 12),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [members, mainCurrency, ratesTick]
  );

  const totalBalance = members.reduce((s, b) => s + (bucketBalancesMain[b.id] || 0), 0);
  const allocation = members
    .map((b, i) => ({ id: b.id, name: b.name, value: Math.max(0, bucketBalancesMain[b.id] || 0), color: CHART_COLORS[i % CHART_COLORS.length] }))
    .filter(x => x.value > 0);
  const hasInterest = monthlyInterest.some(m => m.interest !== 0);

  const ytdReturnPct = totalBalance > 0 ? (win.ytd / totalBalance) * 100 : null;

  return (
    <Modal title={groupName} onClose={onClose} wide footer={<button className="btn" onClick={onClose}>Close</button>}>
      {/* Summary stats — mirrors the regular bucket detail view */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 20 }}>
        <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
          <div className="muted" style={{ fontSize: 11 }}>Balance</div>
          <div className="mono savings-text" style={{ fontSize: 18, fontWeight: 500 }}>{fmt(totalBalance)}</div>
        </div>
        <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
          <div className="muted" style={{ fontSize: 11 }}>Interest to date</div>
          <div className="mono savings-text" style={{ fontSize: 18, fontWeight: 500 }}>+{fmt(win.total)}</div>
        </div>
        <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
          <div className="muted" style={{ fontSize: 11 }}>YTD interest</div>
          <div className="mono savings-text" style={{ fontSize: 18, fontWeight: 500 }}>+{fmt(win.ytd)}</div>
          {ytdReturnPct != null && <div className="muted" style={{ fontSize: 11 }}>{ytdReturnPct.toFixed(1)}% return</div>}
        </div>
      </div>

      {/* Balance history */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)' }}>
            <TrendingUp size={12} /> Balance history ({mainCurrency})
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {LOOKBACKS.map(k => (
              <button key={k} className={`btn btn-xs ${lookback === k ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setLookback(k)}>{k}</button>
            ))}
          </div>
        </div>

        {lookback === 'Custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', fontSize: 12, marginBottom: 10 }}>
            <span className="muted">From</span>
            <input type="date" value={customFrom} max={customTo} onChange={e => setCustomFrom(e.target.value)} style={{ padding: '4px 8px', fontSize: 12 }} />
            <span className="muted">to</span>
            <input type="date" value={customTo} min={customFrom} max={todayIso} onChange={e => setCustomTo(e.target.value)} style={{ padding: '4px 8px', fontSize: 12 }} />
          </div>
        )}

        <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
          Interest this range <span className="mono savings-text">+{fmt(windowInterest)}</span>
          {' · '}YTD <span className="mono savings-text">+{fmt(win.ytd)}</span>
        </div>

        {history.length > 1 ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={history} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: 'var(--text-tertiary)' }} />
              <YAxis tick={{ fontSize: 9, fill: 'var(--text-tertiary)' }} tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
              <Line type="monotone" dataKey="balance" stroke="#3b82f6" strokeWidth={2} dot={false} name={`Balance (${mainCurrency})`} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="muted" style={{ fontSize: 12, padding: '8px 0' }}>Not enough data for this range.</div>
        )}
      </div>

      {/* Interest over time */}
      {hasInterest && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)', marginBottom: 10 }}>
            Interest per month ({mainCurrency})
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={monthlyInterest} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: 'var(--text-tertiary)' }} />
              <YAxis tick={{ fontSize: 9, fill: 'var(--text-tertiary)' }} tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
              <Bar dataKey="interest" fill="#10b981" name={`Interest (${mainCurrency})`} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Allocation */}
      {allocation.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)', marginBottom: 10 }}>
            Allocation
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <ResponsiveContainer width={150} height={150}>
              <PieChart>
                <Pie data={allocation} cx="50%" cy="50%" innerRadius={38} outerRadius={66} dataKey="value" paddingAngle={2}>
                  {allocation.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={v => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {allocation.map(d => (
                <div
                  key={d.id}
                  onClick={() => onOpenBucket(d.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}
                  title="Open bucket"
                >
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: d.color, flexShrink: 0 }} />
                  <span>{d.name}</span>
                  <span className="mono savings-text">{fmt(d.value)}</span>
                  <span className="muted">({totalBalance > 0 ? Math.round((d.value / totalBalance) * 100) : 0}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ─── Bucket Detail Modal ─── */
function BucketDetailModal({
  bucket: b, bal, balMain, mainCurrency, bucketIdx, transactions = [],
  onClose, onAddAdjustment, onDeleteAdjustment, onUpdateAdjustment, onDeleteTransaction, onEditTransaction, onEdit,
  fmt, fmtFor, settings, ratesTick,
}) {
  const [editAdj, setEditAdj] = useState(null);
  const pct   = b.target > 0 ? Math.min(100, Math.round((bal / b.target) * 100)) : null;
  const ml    = b.target && b.monthlyTarget ? monthsToGoal(bal, b.target, b.monthlyTarget) : null;
  const color = CHART_COLORS[bucketIdx >= 0 ? bucketIdx % CHART_COLORS.length : 0];
  const isForeign = b.currency && b.currency !== mainCurrency;
  const fb = v => fmtFor ? fmtFor(b, v) : fmt(v);
  const isPhone = useIsPhone();

  const LOOKBACKS = ['1M', '3M', '6M', '1Y', '2Y', '5Y', 'YTD'];
  const [lookback, setLookback] = useState('1Y');
  const [customUntil, setCustomUntil] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  });

  const annualRate     = useMemo(() => inferAnnualInterestRate(b, transactions), [b, transactions]);
  const actualInterest = useMemo(() => getActualInterest(b),                      [b]);
  const history        = useMemo(
    () => getHistoricalBalanceSeries(b, transactions, lookback, { mainCurrency }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [b, transactions, lookback, mainCurrency, ratesTick]
  );

  // User-entered "Expected APY" — drives the projection when set. Empty string = use inferred.
  const [expectedApyStr, setExpectedApyStr] = useState('');
  const expectedApyNum = parseFloat(expectedApyStr);
  const effectiveRate =
    Number.isFinite(expectedApyNum) && expectedApyNum >= 0
      ? expectedApyNum / 100
      : annualRate;

  const projection = useMemo(() => {
    const opts = { annualRate: effectiveRate, convertToMain: true, mainCurrency };
    if (!b.target) opts.untilDate = customUntil;
    return b.monthlyTarget > 0 || effectiveRate > 0 ? getBucketProjection(b, bal, opts) : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b, bal, effectiveRate, customUntil, mainCurrency, ratesTick]);

  const todayStr = new Date().toISOString().slice(0, 10);

  const goalReachedAt = b.target > 0 && projection.length
    ? projection.find(p => p.balance >= b.target)
    : null;
  const projectedFinalBalance = projection.length
    ? projection[projection.length - 1].balance
    : bal;
  const projectedFinalBalanceMain = projection.length
    ? projection[projection.length - 1].balanceMain
    : null;

  // Savings transactions linked to this bucket, newest first
  const bucketTxns = [...transactions]
    .filter(t => t.type === 'savings' && t.bucket === b.id)
    .sort((a, b) => b.date.localeCompare(a.date) || b.id.localeCompare(a.id));

  return (
    <Modal
      title={b.name}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>Close</button>
          {!b.archived && (
            <button className="btn btn-ghost" onClick={onEdit}>
              <Edit2 size={13} /> Edit
            </button>
          )}
          {!b.archived && (
            <button className="btn btn-primary" onClick={onAddAdjustment}>
              <Plus size={13} /> Adjustment
            </button>
          )}
        </>
      }
    >
      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 20 }}>
        <div
          style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8, cursor: b.archived ? 'default' : 'pointer', userSelect: 'none' }}
          title={b.archived ? undefined : 'Double-click to set balance'}
          onDoubleClick={() => { if (!b.archived) onAddAdjustment({ initialType: 'set-balance' }); }}
        >
          <div className="muted" style={{ fontSize: 11 }}>Balance{isForeign ? ` (${b.currency})` : ''}</div>
          <div className="mono savings-text" style={{ fontSize: 18, fontWeight: 500 }}>{fb(bal)}</div>
          {isForeign && typeof balMain === 'number' && <div className="muted" style={{ fontSize: 11 }}>≈ {fmt(balMain)}</div>}
        </div>
        {b.target > 0 && (
          <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
            <div className="muted" style={{ fontSize: 11 }}>Goal</div>
            <div className="mono" style={{ fontSize: 18, fontWeight: 500 }}>{fb(b.target)}</div>
          </div>
        )}
        {b.monthlyTarget > 0 && (
          <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
            <div className="muted" style={{ fontSize: 11 }}>Monthly target</div>
            <div className="mono" style={{ fontSize: 18, fontWeight: 500 }}>{fb(b.monthlyTarget)}</div>
          </div>
        )}
        {ml !== null && (
          <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
            <div className="muted" style={{ fontSize: 11 }}>Months left</div>
            <div className="mono" style={{ fontSize: 18, fontWeight: 500 }}>{ml === 0 ? 'Done ✓' : `~${ml}`}</div>
          </div>
        )}
      </div>

      {/* Progress bar */}
      {b.target > 0 && pct !== null && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
            <span className="muted">Progress toward goal</span>
            <span className="mono savings-text">{pct}%</span>
          </div>
          <ProgressBar value={bal} max={b.target} color={color} />
        </div>
      )}

      {/* History chart — actual balance only, ends at today */}
      {history.length > 1 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)' }}>
              <TrendingUp size={12} /> Balance history
              {isForeign && <span className="muted" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>— values in {mainCurrency} (monthly FX)</span>}
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {LOOKBACKS.map(k => (
                <button
                  key={k}
                  className={`btn btn-xs ${lookback === k ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setLookback(k)}
                >{k}</button>
              ))}
            </div>
          </div>

          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={history} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: 'var(--text-tertiary)' }} />
              <YAxis tick={{ fontSize: 9, fill: 'var(--text-tertiary)' }} tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
              <Line type="monotone" dataKey={isForeign ? 'balanceMain' : 'balance'} stroke={color} strokeWidth={2} dot={false} name={`Balance${isForeign ? ` (${mainCurrency})` : ''}`} />
              {b.target > 0 && !isForeign && <ReferenceLine y={b.target} stroke="var(--expense)" strokeDasharray="4 2" label={{ value: 'Goal', fill: 'var(--expense)', fontSize: 10 }} />}
              {b.target > 0 && isForeign && (() => {
                const r = getLatestRate(b.currency, mainCurrency);
                return r != null ? <ReferenceLine y={b.target * r} stroke="var(--expense)" strokeDasharray="4 2" label={{ value: `Goal (today)`, fill: 'var(--expense)', fontSize: 10 }} /> : null;
              })()}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Forecast — text only */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)' }}>
            Forecast
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, flexWrap: 'wrap' }}>
            {!b.target && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="muted">Project until:</span>
                <input
                  type="date"
                  value={customUntil}
                  min={todayStr}
                  onChange={e => setCustomUntil(e.target.value)}
                  style={{ padding: '4px 8px', fontSize: 12 }}
                />
              </label>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="muted">Expected APY:</span>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder={annualRate > 0 ? (annualRate * 100).toFixed(2) : '0'}
                value={expectedApyStr}
                onChange={e => setExpectedApyStr(e.target.value)}
                aria-label="Expected APY (percent)"
                style={{ padding: '4px 8px', fontSize: 12, width: 70 }}
              />
              <span className="muted">%</span>
            </label>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
            <div className="muted" style={{ fontSize: 11 }}>APY (inferred)</div>
            <div className="mono" style={{ fontSize: 16, fontWeight: 500 }}>
              {annualRate > 0 ? `${(annualRate * 100).toFixed(2)}%` : '—'}
            </div>
          </div>
          <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
            <div className="muted" style={{ fontSize: 11 }}>Actual interest to date</div>
            <div className="mono savings-text" style={{ fontSize: 16, fontWeight: 500 }}>{fb(actualInterest)}</div>
          </div>
          {!b.target && projection.length > 0 && (
            <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
              <div className="muted" style={{ fontSize: 11 }}>Projected balance on date</div>
              <div className="mono savings-text" style={{ fontSize: 16, fontWeight: 500 }}>{fb(projectedFinalBalance)}</div>
              {isForeign && typeof projectedFinalBalanceMain === 'number' && (
                <div className="muted" style={{ fontSize: 11 }}>≈ {fmt(projectedFinalBalanceMain)} (today's rate)</div>
              )}
            </div>
          )}
          {goalReachedAt && (
            <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
              <div className="muted" style={{ fontSize: 11 }}>Goal reached</div>
              <div className="mono" style={{ fontSize: 16, fontWeight: 500 }}>{goalReachedAt.name}</div>
            </div>
          )}
          {b.target > 0 && projection.length > 0 && !goalReachedAt && (
            <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
              <div className="muted" style={{ fontSize: 11 }}>Goal reached</div>
              <div className="mono" style={{ fontSize: 16, fontWeight: 500 }}>&gt; 5 years</div>
            </div>
          )}
        </div>
      </div>

      {/* Savings transactions */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)', marginBottom: 10 }}>
          Contributions ({bucketTxns.length})
        </div>
        {bucketTxns.length > 0 ? (
          isPhone ? (
            <CardList
              rows={bucketTxns}
              rowKey={t => t.id}
              columns={[
                { slot: 'title', render: t => t.description },
                { slot: 'sub',   render: t => formatDate(t.date, settings) },
                { slot: 'value', render: t => {
                    const txnIsForeign = isForeign && t.currency === b.currency && Number.isFinite(t.foreignAmount);
                    const primary = txnIsForeign ? fb(t.foreignAmount) : fmt(t.amount);
                    return <>+{primary}{txnIsForeign && <span className="list-row-value-sub">= {fmt(t.amount)}</span>}</>;
                  }, valueClass: () => 'savings-text' },
              ]}
              onTap={onEditTransaction ? t => onEditTransaction(t) : undefined}
              slideLeft={onDeleteTransaction ? [{
                label: 'Delete', icon: <Trash2 size={16} />, color: 'danger',
                confirm: { title: 'Delete transaction', message: 'Delete this contribution? This cannot be undone.', danger: true },
                onAction: t => onDeleteTransaction(t.id),
              }] : []}
            />
          ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {bucketTxns.map(t => {
                  const txnIsForeign = isForeign && t.currency === b.currency && Number.isFinite(t.foreignAmount);
                  const primary = txnIsForeign ? fb(t.foreignAmount) : fmt(t.amount);
                  return (
                    <tr key={t.id}>
                      <td className="muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(t.date, settings)}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{t.description}</td>
                      <td className="amount-cell mono savings-text">
                        +{primary}
                        {txnIsForeign && <div className="muted" style={{ fontSize: 11 }}>= {fmt(t.amount)}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )
        ) : (
          <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-tertiary)', fontSize: 13 }}>
            No contributions yet — log a savings transaction in the Tracker tab to add funds.
          </div>
        )}
      </div>

      {/* Adjustment history */}
      {(b.adjustments || []).length > 0 ? (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)', marginBottom: 10 }}>
            Adjustment history
          </div>
          {isPhone ? (
            <CardList
              rows={[...(b.adjustments || [])].reverse()}
              rowKey={a => a.id}
              columns={[
                { slot: 'title', render: a => <><span className={`badge badge-${a.type}`}>{a.type}</span>{a.note ? <span style={{ marginLeft: 8 }}>{a.note}</span> : null}</> },
                { slot: 'sub',   render: a => formatDate(a.date, settings) },
                { slot: 'value', render: a => <>{a.amount >= 0 ? '+' : ''}{fb(a.amount)}</>, valueClass: a => a.amount >= 0 ? 'savings-text' : 'expense-text' },
              ]}
              onTap={onUpdateAdjustment ? a => setEditAdj(a) : undefined}
              slideLeft={[{
                label: 'Delete', icon: <Trash2 size={16} />, color: 'danger',
                confirm: { title: 'Delete adjustment', message: 'Delete this adjustment? This cannot be undone.', danger: true },
                onAction: a => onDeleteAdjustment(b.id, a.id),
              }]}
            />
          ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {[...(b.adjustments || [])].reverse().map(a => (
                  <tr key={a.id}>
                    <td className="muted">{formatDate(a.date, settings)}</td>
                    <td><span className={`badge badge-${a.type}`}>{a.type}</span></td>
                    <td className={`amount-cell mono ${a.amount >= 0 ? 'savings-text' : 'expense-text'}`}>
                      {a.amount >= 0 ? '+' : ''}{fb(a.amount)}
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>{a.note || '—'}</td>
                    <td>
                      <button
                        className="btn btn-ghost btn-icon btn-xs btn-danger"
                        onClick={() => onDeleteAdjustment(b.id, a.id)}
                        title="Delete"
                      >
                        <Trash2 size={10} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-tertiary)', fontSize: 13 }}>
          No adjustments yet — use "+ Adjustment" to log interest, bonuses, or corrections.
        </div>
      )}

      {editAdj && (
        <EditAdjustmentModal
          adj={editAdj}
          onSave={updates => { onUpdateAdjustment(b.id, editAdj.id, updates); setEditAdj(null); }}
          onClose={() => setEditAdj(null)}
        />
      )}
    </Modal>
  );
}

/* ─── Edit adjustment modal (phone tap-to-edit) ─── */
function EditAdjustmentModal({ adj, onSave, onClose }) {
  const ADJ_TYPES = ['interest', 'bonus', 'correction', 'withdrawal', 'fee'];
  const [form, setForm] = useState({
    type: adj.type || 'interest',
    amount: String(adj.amount ?? ''),
    note: adj.note || '',
    date: adj.date || todayStr(),
  });
  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const amt = parseFloat(form.amount);

  function save() {
    onSave({ type: form.type, amount: Number.isFinite(amt) ? amt : 0, note: form.note, date: form.date });
  }

  return (
    <Modal
      title="Edit adjustment"
      variant="sheet"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={!Number.isFinite(amt)}>Save changes</button>
        </>
      }
    >
      <div className="form-group">
        <label>Type</label>
        <select value={form.type} onChange={e => upd('type', e.target.value)}>
          {ADJ_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>Amount (negative for withdrawals/fees)</label>
        <input type="number" step="0.01" inputMode="decimal"
          value={form.amount} onChange={e => upd('amount', e.target.value)} />
      </div>
      <div className="form-group">
        <label>Date</label>
        <input type="date" value={form.date} onChange={e => upd('date', e.target.value)} />
      </div>
      <div className="form-group">
        <label>Note</label>
        <input value={form.note} onChange={e => upd('note', e.target.value)} placeholder="optional" />
      </div>
    </Modal>
  );
}

/* ─── Add / Edit Bucket Modal ─── */
export function AddBucketModal({ initial, onSave, onClose, mainCurrency, groups = [] }) {
  const { theme } = useBudget();
  const [form, setForm] = useState({
    name:           initial?.name           || '',
    emoji:          initial?.emoji          || '',
    group:          initial?.group          || '',
    target:         initial?.target         || '',
    monthlyTarget:  initial?.monthlyTarget  || '',
    initialBalance: initial?.initialBalance || '',
    priority:       initial?.priority       || 3,
    currency:       initial?.currency       || mainCurrency,
  });
  function setF(k, v) { setForm(p => ({ ...p, [k]: v })); }
  const [showEmoji, setShowEmoji] = useState(false);
  const isForeign = form.currency && form.currency !== mainCurrency;
  const symb = getCurrencyInfo(form.currency)?.symbol || form.currency;
  function submit() {
    if (!form.name.trim()) return;
    const out = {
      ...form,
      group:          form.group.trim(),
      target:         parseFloat(form.target)         || 0,
      monthlyTarget:  parseFloat(form.monthlyTarget)  || 0,
      initialBalance: parseFloat(form.initialBalance) || 0,
      priority:       parseInt(form.priority)         || 3,
      currency:       form.currency || mainCurrency,
    };
    // Don't persist currency field when it matches main (keeps bucket JSON clean)
    if (out.currency === mainCurrency) delete out.currency;
    // On edit, send group:'' explicitly so a removal overwrites the stored value
    // (the reducer merges updates, so a deleted key would keep the old group).
    if (!out.group) { if (initial) out.group = ''; else delete out.group; }
    // On edit, send emoji:'' explicitly so a removal overwrites the stored value
    // (the reducer merges updates, so a deleted key would keep the old emoji).
    if (!out.emoji) { if (initial) out.emoji = ''; else delete out.emoji; }
    onSave(out);
  }
  return (
    <Modal
      title={initial ? 'Edit bucket' : 'New savings bucket'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit}>{initial ? 'Save' : 'Create'}</button>
        </>
      }
    >
      <div className="form-group bucket-name-row">
        <button
          type="button"
          className={`bucket-icon-chip${form.emoji ? '' : ' empty'}`}
          aria-label={form.emoji ? 'Change icon' : 'Choose icon'}
          aria-expanded={showEmoji}
          onClick={() => setShowEmoji(s => !s)}
        >
          {form.emoji}
        </button>
        <div className="bucket-name-field">
          <label>Name</label>
          <input value={form.name} onChange={e => setF('name', e.target.value)} placeholder="e.g. House Fund" autoFocus />
        </div>
      </div>
      {showEmoji && (
        <div className="emoji-picker-panel">
          {form.emoji && (
            <button type="button" className="btn btn-ghost btn-sm" style={{ marginBottom: 8 }}
              onClick={() => { setF('emoji', ''); setShowEmoji(false); }}>
              <Trash2 size={12} /> Remove icon
            </button>
          )}
          <EmojiPicker
            onEmojiClick={(e) => { setF('emoji', e.emoji); setShowEmoji(false); }}
            emojiStyle={EmojiStyle.NATIVE}
            theme={theme === 'dark' ? Theme.DARK : Theme.LIGHT}
            width="100%"
            height={340}
            lazyLoadEmojis
            previewConfig={{ showPreview: false }}
          />
        </div>
      )}
      <div className="form-group">
        <label>Group (optional)</label>
        <SearchableSelect
          value={form.group}
          onChange={val => setF('group', val)}
          onCreateOption={name => setF('group', name)}
          createLabel="as new group"
          options={[{ value: '', label: 'No group' }].concat(
            [...new Set([...groups, form.group].map(g => g.trim()).filter(Boolean))]
              .map(g => ({ value: g, label: g }))
          )}
          placeholder="No group"
        />
      </div>
      <div style={{ display: 'flex', gap: 10 }} className="form-group">
        <div style={{ flex: '0 0 110px' }}>
          <label>Priority (1–5)</label>
          <input type="number" min="1" max="5" value={form.priority} onChange={e => setF('priority', e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label>Currency</label>
          <SearchableSelect
            value={form.currency}
            onChange={code => setF('currency', code)}
            options={getAllCurrencies().map(c => ({ value: c.code, label: `${c.name} (${c.code})`, secondary: c.symbol }))}
            placeholder="Currency…"
          />
        </div>
      </div>
      {isForeign && (
        <div className="muted" style={{ fontSize: 12, marginTop: -6, marginBottom: 10 }}>
          Amounts below are in {form.currency}. Exchange rates to {mainCurrency} are fetched automatically from ECB daily rates.
        </div>
      )}
      <div style={{ display: 'flex', gap: 10 }} className="form-group">
        <div style={{ flex: 1 }}>
          <label>Goal amount{isForeign ? ` (${symb})` : ''}</label>
          <input type="number" value={form.target} onChange={e => setF('target', e.target.value)} placeholder="0.00" min="0" />
        </div>
        <div style={{ flex: 1 }}>
          <label>Monthly target{isForeign ? ` (${symb})` : ''}</label>
          <input type="number" value={form.monthlyTarget} onChange={e => setF('monthlyTarget', e.target.value)} placeholder="0.00" min="0" />
        </div>
      </div>
      <div className="form-group">
        <label>Starting balance (optional){isForeign ? ` — ${symb}` : ''}</label>
        <input type="number" value={form.initialBalance} onChange={e => setF('initialBalance', e.target.value)} placeholder="0.00" min="0" />
      </div>
    </Modal>
  );
}

/* ─── Calculator helpers (pure, exported for tests) ─── */
export function computeLoanPayment(amount, annualRatePct, months) {
  const a = Number(amount), rp = Number(annualRatePct), n = Number(months);
  if (!(a > 0) || !(n > 0) || rp < 0) return null;
  const r = rp / 100 / 12;
  if (r === 0) return a / n;
  return (a * r) / (1 - Math.pow(1 + r, -n));
}

export function computeFutureValue(monthly, annualRatePct, years) {
  const m = Number(monthly), rp = Number(annualRatePct), y = Number(years);
  if (!(m > 0) || !(y > 0) || rp < 0) return null;
  const n = y * 12;
  const r = rp / 100 / 12;
  if (r === 0) return m * n;
  return (m * (Math.pow(1 + r, n) - 1)) / r;
}

/* ─── Loan Calculator Modal ─── */
function LoanCalculatorModal({ fmt, onClose }) {
  const [form, setForm] = useState({ amount: '', rate: '', months: '' });
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const monthly = useMemo(() => computeLoanPayment(form.amount, form.rate, form.months), [form]);
  const months = parseFloat(form.months) || 0;
  const amount = parseFloat(form.amount) || 0;
  const totalPaid = monthly != null ? monthly * months : null;
  const totalInterest = totalPaid != null ? totalPaid - amount : null;

  return (
    <Modal
      title="Loan calculator"
      onClose={onClose}
      footer={<button className="btn" onClick={onClose}>Close</button>}
    >
      <div className="form-group">
        <label>Loan amount</label>
        <input type="number" min="0" step="0.01" value={form.amount} onChange={e => setF('amount', e.target.value)} placeholder="0.00" autoFocus />
      </div>
      <div style={{ display: 'flex', gap: 10 }} className="form-group">
        <div style={{ flex: 1 }}>
          <label>Annual interest rate (%)</label>
          <input type="number" min="0" step="0.01" value={form.rate} onChange={e => setF('rate', e.target.value)} placeholder="0.00" />
        </div>
        <div style={{ flex: 1 }}>
          <label>Term (months)</label>
          <input type="number" min="1" max="600" step="1" value={form.months} onChange={e => setF('months', e.target.value)} placeholder="24" />
        </div>
      </div>
      <div className="card" style={{ background: 'var(--bg-secondary)', marginTop: 12 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)', marginBottom: 4 }}>Monthly payment</div>
        <div className="mono" style={{ fontSize: 28, fontWeight: 500 }}>{monthly != null ? fmt(monthly) : '—'}</div>
        {monthly != null && (
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            Total paid {fmt(totalPaid)} · Total interest {fmt(totalInterest)}
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ─── Savings Predictor Modal ─── */
function SavingsPredictorModal({ fmt, onClose }) {
  const [form, setForm] = useState({ monthly: '', rate: '', years: '', inflation: '3' });
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const fv = useMemo(() => computeFutureValue(form.monthly, form.rate, form.years), [form]);
  const years = parseFloat(form.years) || 0;
  const monthly = parseFloat(form.monthly) || 0;
  const inflation = parseFloat(form.inflation);
  const deposited = fv != null ? monthly * years * 12 : null;
  const interest = fv != null ? fv - deposited : null;
  const fvReal = (fv != null && inflation > 0 && years > 0)
    ? fv / Math.pow(1 + inflation / 100, years)
    : null;

  return (
    <Modal
      title="Savings predictor"
      onClose={onClose}
      footer={<button className="btn" onClick={onClose}>Close</button>}
    >
      <div className="form-group">
        <label>Monthly deposit</label>
        <input type="number" min="0" step="0.01" value={form.monthly} onChange={e => setF('monthly', e.target.value)} placeholder="0.00" autoFocus />
      </div>
      <div style={{ display: 'flex', gap: 10 }} className="form-group">
        <div style={{ flex: 1 }}>
          <label>Annual interest (%)</label>
          <input type="number" min="0" step="0.01" value={form.rate} onChange={e => setF('rate', e.target.value)} placeholder="0.00" />
        </div>
        <div style={{ flex: 1 }}>
          <label>Years</label>
          <input type="number" min="1" max="50" step="1" value={form.years} onChange={e => setF('years', e.target.value)} placeholder="10" />
        </div>
      </div>
      <div className="form-group">
        <label>Inflation rate (%/yr)</label>
        <input type="number" min="0" step="0.01" value={form.inflation} onChange={e => setF('inflation', e.target.value)} placeholder="3" style={{ maxWidth: 140 }} />
      </div>
      <div className="card" style={{ background: 'var(--bg-secondary)', marginTop: 12 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)', marginBottom: 4 }}>End-of-term savings</div>
        <div className="mono savings-text" style={{ fontSize: 28, fontWeight: 500 }}>{fv != null ? fmt(fv) : '—'}</div>
        {fvReal != null && (
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            ≈ {fmt(fvReal)} in today's money ({inflation}% inflation)
          </div>
        )}
        {fv != null && (
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            Total deposited {fmt(deposited)} · Interest earned {fmt(interest)}
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ─── Adjustment Modal ─── */
function AdjustmentModal({ bucket, bucketName, currentBalance = 0, initialType, onSave, onClose, fmt }) {
  const [form, setForm] = useState({ type: initialType || 'interest', amount: '', note: '', date: todayStr() });
  const [asInterest, setAsInterest] = useState(true);
  function setF(k, v) { setForm(p => ({ ...p, [k]: v })); }

  const isSetBalance = form.type === 'set-balance';
  const targetBalance = parseFloat(form.amount);
  const delta = Number.isFinite(targetBalance) ? targetBalance - currentBalance : null;
  const deltaType = asInterest ? 'interest' : 'adjustment';
  const ccy = bucket?.currency;
  const symb = ccy ? (getCurrencyInfo(ccy)?.symbol || ccy) : null;

  function submit() {
    if (form.amount === '' || form.amount == null) return;
    if (isSetBalance) {
      if (!Number.isFinite(targetBalance) || delta === 0) return;
      const note = form.note || `Set balance to ${fmt(targetBalance)}`;
      onSave({
        type: deltaType,
        amount: parseFloat(delta.toFixed(2)),
        note,
        date: form.date,
      });
      return;
    }
    const amt = parseFloat(form.amount) * (form.type === 'withdrawal' ? -1 : 1);
    onSave({ ...form, amount: parseFloat(amt.toFixed(2)) });
  }

  return (
    <Modal
      title={`Add to ${bucketName}`}
      onClose={onClose}
      footer={
        <>
          {isSetBalance && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', marginRight: 'auto' }}>
              <input type="checkbox" checked={asInterest} onChange={e => setAsInterest(e.target.checked)} style={{ flexShrink: 0, margin: 0 }} />
              <span>Treat difference as interest</span>
            </label>
          )}
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit}>{isSetBalance ? 'Set balance' : 'Add'}</button>
        </>
      }
    >
      <div className="form-group">
        <label>Type</label>
        <select value={form.type} onChange={e => setF('type', e.target.value)}>
          <option value="interest">Interest</option>
          <option value="adjustment">Adjustment</option>
          <option value="correction">Correction</option>
          <option value="bonus">Bonus</option>
          <option value="withdrawal">Withdrawal</option>
          <option value="set-balance">Set balance to…</option>
        </select>
      </div>
      <div className="form-group">
        <label>{isSetBalance ? 'New balance' : 'Amount'}{symb ? ` (${symb})` : ''}</label>
        <input
          type="number"
          value={form.amount}
          onChange={e => setF('amount', e.target.value)}
          placeholder={isSetBalance ? currentBalance.toFixed(2) : '0.00'}
          min={isSetBalance ? undefined : '0'}
          step="0.01"
          autoFocus
        />
        {isSetBalance && (
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            Current: <span className="mono">{fmt(currentBalance)}</span>
            {delta !== null && delta !== 0 && (
              <> · Difference recorded as {deltaType}: <span className={`mono ${delta >= 0 ? 'savings-text' : 'expense-text'}`}>
                {delta >= 0 ? '+' : ''}{fmt(delta)}
              </span></>
            )}
          </div>
        )}
        {form.type === 'withdrawal' && (
          <div className="error-msg" style={{ color: 'var(--text-tertiary)' }}>Will be subtracted from balance</div>
        )}
      </div>
      <div className="form-group">
        <label>Date</label>
        <input type="date" value={form.date} onChange={e => setF('date', e.target.value)} />
      </div>
      <div className="form-group">
        <label>Note (optional)</label>
        <input value={form.note} onChange={e => setF('note', e.target.value)} placeholder={isSetBalance ? `Set balance to ${fmt(targetBalance || currentBalance)}` : 'e.g. 3% annual APY'} />
      </div>
    </Modal>
  );
}

export default React.memo(Savings);
